import React, { useEffect, useState } from 'react';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Now that you understand the full outreach workflow—from identifying strong prospects and finding them through the right channels to messaging them and tracking their progress—let’s cover what is expected of you each month. You have two monthly expectations: bring in at least ten qualified referrals and attend at least one Tribe gathering yourself. We require ten so ambassadors are consistently bringing in new people into Tribe, rather than only sending an occasional invite. Attending a gathering helps you understand the experience firsthand, making it easier to explain Tribe to the people you invite.',
  },
  {
  type: 'question',

  prompt:
    '12 people signed up using your referral code. 7 of them attended their first Tribe gathering. How many qualified referrals do you have?',

  choices: [
    { value: 'A', label: '12' },
    { value: 'B', label: '7' },
    { value: 'C', label: '19' },
  ],

  correct: 'B',

  explanation:
    'A referral becomes qualified only after they sign up with your code and attend their first gathering.',
},
{
  type: 'question',

  prompt:
    'Why should ambassadors attend a gathering monthly?',

  choices: [
    {
      value: 'A',
      label:
        'To understand the member experience and speak about it naturally',
    },
    {
      value: 'B',
      label:
        'To qualify their own referral',
    },
    {
      value: 'C',
      label:
        'To meet more people they can recruit during the gathering',
    },
  ],

  correct: 'A',

  explanation:
    'Firsthand experience helps you explain Tribe more naturally and accurately.',
},
];

const CAPTION_TIMINGS = [
  1500,
  3100,
  4800,
  5900,
  7000,
  8200,
  9400,
  11000,
  12400,
  14000,
  15100,
  18000,
  19500,
  27500,
  36000,
];

const CAPTIONS = [
  'Now that you understand',
  'the full outreach workflow',
  'from identifying strong prospects',
  'and finding them',
  'through the right channels',
  'to messaging them',
  'and tracking their progress',
  'let’s cover what is expected',
  'of you each month',
  'you have two monthly expectations',
];

export default function MonthlyExpectations({
  part,
  Question,
  selected,
  onSelect,
  audioStatus,
  playbackId,
}) {
  const step = STEPS[part];
  const [visualStage, setVisualStage] = useState(0);

  useEffect(() => {
    if (
      step?.type !== 'lesson' ||
      audioStatus !== 'Playing'
    ) {
      return undefined;
    }

    setVisualStage(0);

    const timers = CAPTION_TIMINGS.map(
      (time, index) =>
        window.setTimeout(
          () => setVisualStage(index + 1),
          time
        )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [part, step?.type, audioStatus, playbackId]);

  useEffect(() => {
    if (
      step?.type === 'lesson' &&
      audioStatus === 'Complete'
    ) {
      setVisualStage(CAPTION_TIMINGS.length);
    }
  }, [step?.type, audioStatus]);

  if (step.type === 'question') {
    return (
      <Question
        {...step}
        selected={selected}
        onSelect={onSelect}
      />
    );
  }

  const caption =
    CAPTIONS[Math.min(visualStage, CAPTIONS.length - 1)];
  const captionIndex = Math.min(
    visualStage,
    CAPTIONS.length - 1
  );
  const nextCaptionTime =
    CAPTION_TIMINGS[captionIndex] ||
    CAPTION_TIMINGS[CAPTION_TIMINGS.length - 1];
  const previousCaptionTime =
    captionIndex === 0
      ? 0
      : CAPTION_TIMINGS[captionIndex - 1];
  const captionDuration =
    nextCaptionTime - previousCaptionTime;
  const copyLocked = visualStage >= 10;
  const requirementsVisible = visualStage >= 10;
  const requirementFocus =
    visualStage >= 15
      ? 'both'
      : visualStage >= 14
        ? 'event'
        : visualStage >= 13
          ? 'referrals'
          : 'both';

  return (
    <section
      className={[
        'monthly-minimal-slide',
        copyLocked ? 'is-locked' : 'is-centered',
      ].join(' ')}
    >
      <style>{STYLES}</style>

      <div className="monthly-minimal-copy">
        <WordCaption
          text={caption}
          duration={captionDuration}
        />
      </div>

      <div className="monthly-minimal-visual">
        <div className="requirements-stack">
          <Requirement
            number="10"
            label="qualified referrals"
            visible={requirementsVisible}
            emphasis={requirementFocus === 'referrals'
              ? 'focused'
              : requirementFocus === 'event'
                ? 'muted'
                : 'equal'}
          />

          <Requirement
            number="1"
            label="Tribe gathering"
            visible={requirementsVisible}
            emphasis={requirementFocus === 'event'
              ? 'focused'
              : requirementFocus === 'referrals'
                ? 'muted'
                : 'equal'}
          />
        </div>
      </div>
    </section>
  );
}

function WordCaption({ text, duration }) {
  const words = text.split(' ');
  const denominator = Math.max(words.length - 1, 1);
  const wordDelay = Math.max(
    100,
    Math.min(230, (duration - 250) / denominator)
  );

  return (
    <h1 key={text} aria-label={text}>
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          <span
            className="caption-word"
            style={{
              '--caption-word-delay': `${index * wordDelay}ms`,
            }}
            aria-hidden="true"
          >
            {word}
          </span>

          {index < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </h1>
  );
}

function Requirement({
  number,
  label,
  visible,
  emphasis,
}) {
  return (
    <div
      className={[
        'requirement-lockup',
        visible ? 'is-visible' : '',
        emphasis === 'focused' ? 'is-focused' : '',
        emphasis === 'muted' ? 'is-muted' : '',
      ].join(' ')}
    >
      <strong className="requirement-number">
        {number}
      </strong>

      <div>
        <span>{label}</span>
        <small>PER MONTH</small>
      </div>

      <i />
    </div>
  );
}

MonthlyExpectations.title = 'Monthly expectations';
MonthlyExpectations.steps = STEPS;

const STYLES = `
  .monthly-minimal-slide {
    position: relative;
    min-height: 450px;
    width: 100%;
    overflow: hidden;
    color: #111;
  }

  .monthly-minimal-copy {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    width: min(100%, 590px);
    text-align: center;
    transform: translate(-50%, -50%);
    transition:
      left 850ms cubic-bezier(.2,.78,.2,1),
      width 850ms cubic-bezier(.2,.78,.2,1),
      transform 850ms cubic-bezier(.2,.78,.2,1);
  }

  .monthly-minimal-slide.is-locked .monthly-minimal-copy {
    left: 0;
    width: 42%;
    text-align: left;
    transform: translate(0, -50%);
  }

  .monthly-minimal-copy h1 {
    max-width: 520px;
    margin: 0;
    font-size: clamp(38px, 4.35vw, 60px);
    font-weight: 820;
    letter-spacing: -0.052em;
    line-height: 1;
  }

  .monthly-minimal-slide.is-centered .monthly-minimal-copy h1 {
    max-width: none;
  }

  .caption-word {
    display: inline-block;
    opacity: 0;
    transform: translateY(0.42em);
    animation: monthly-word-in 290ms cubic-bezier(.18,.84,.22,1) forwards;
    animation-delay: var(--caption-word-delay);
  }

  .monthly-minimal-visual {
    position: absolute;
    top: 50%;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    transform: translateY(-50%);
  }

  .requirements-stack {
    display: grid;
    width: min(100%, 430px);
  }

  .requirement-lockup {
    position: relative;
    display: grid;
    grid-template-columns:
      clamp(112px, 10vw, 142px)
      minmax(0, 1fr);
    align-items: center;
    column-gap: clamp(18px, 2vw, 28px);
    width: 100%;
    padding: 24px 0 22px;
    opacity: 1;
    transform: translateX(0);
    transition:
      opacity 520ms ease,
      transform 620ms cubic-bezier(.18,.84,.22,1);
  }

  .requirement-lockup.is-focused {
    opacity: 1;
    transform: translateX(5px);
  }

  .requirement-lockup.is-muted {
    opacity: 0.22;
    transform: translateX(0);
  }

  .requirement-number {
    display: block;
    min-width: 0;
    opacity: 0;
    font-family: inherit;
    font-size: clamp(70px, 7.4vw, 96px);
    font-weight: 880;
    letter-spacing: -0.075em;
    line-height: 0.82;
    text-align: center;
    transform: translateY(24px);
    transition:
      opacity 520ms ease,
      transform 780ms cubic-bezier(.18,.84,.22,1);
  }

  .requirement-lockup > div {
    display: grid;
    gap: 7px;
    padding-bottom: 0;
    overflow: hidden;
  }

  .requirement-lockup span,
  .requirement-lockup small {
    opacity: 0;
    transform: translateY(18px);
    transition:
      opacity 450ms ease,
      transform 650ms cubic-bezier(.18,.84,.22,1);
  }

  .requirement-lockup span {
    font-size: clamp(20px, 2.3vw, 29px);
    font-weight: 820;
    letter-spacing: -0.04em;
    line-height: 1;
    transition-delay: 130ms;
  }

  .requirement-lockup small {
    color: #777;
    font-size: 10px;
    font-weight: 760;
    letter-spacing: 0.12em;
    transition-delay: 220ms;
  }

  .requirement-lockup > i {
    grid-column: 1 / -1;
    width: 100%;
    height: 1px;
    margin-top: 24px;
    background: #d8d8d8;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 850ms 300ms cubic-bezier(.18,.84,.22,1);
  }

  .requirement-lockup.is-visible > .requirement-number,
  .requirement-lockup.is-visible span,
  .requirement-lockup.is-visible small {
    opacity: 1;
    transform: translateY(0);
  }

  .requirement-lockup.is-visible > .requirement-number {
    transform: translateY(0);
  }

  .requirement-lockup.is-visible > i {
    transform: scaleX(1);
  }

  @keyframes monthly-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 800px) {
    .monthly-minimal-slide {
      min-height: 520px;
    }

    .monthly-minimal-slide.is-locked .monthly-minimal-copy {
      top: 27%;
      left: 0;
      width: 100%;
      transform: translate(0, -50%);
    }

    .monthly-minimal-visual {
      top: auto;
      right: auto;
      bottom: 12px;
      left: 0;
      justify-content: flex-start;
      width: 100%;
      transform: none;
    }
  }

  @media (max-width: 480px) {
    .monthly-minimal-copy h1 {
      font-size: 42px;
    }

    .requirement-lockup {
      width: 100%;
    }

    .requirement-number {
      font-size: 76px;
    }

    .requirement-lockup span {
      font-size: 18px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .monthly-minimal-copy,
    .monthly-minimal-copy h1,
    .caption-word,
    .requirement-lockup * {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
      transition-delay: 0ms !important;
    }
  }
`;
